"use client";

import {
  useMatchesContext,
  useTeamsContext,
  useTournamentContext,
} from "@/contexts/TournamentContext";
import { useTeamRankingHistory } from "@/hooks/useTeamRankingHistory";
import { useTeamStatistics } from "@/hooks/useTeamStatistics";
import { teamIsOnVacation } from "@/lib/utils";
import { Match, MatchStatus, PublicUser, Team, TeamStatus } from "@/types";
import { Drawer, DrawerContent, Spinner } from "@heroui/react";
import { format } from "date-fns";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useMemo } from "react";
import { AchievementBadges } from "../AchievementBadges";
import { ChallengeButton } from "../ChallengeButton";
import { MatchResultRow } from "../MatchResultRow";
import { CooldownCounter } from "./CooldownCounter";
import { TeamDrawerCards } from "./TeamDrawerCards";
import { TeamDrawerPlayers } from "./TeamDrawerPlayers";
import { TeamStatisticsComparison } from "./TeamStats";
import { TeamStatusCard } from "./TeamStatusCard";
import { TeamVacationPeriods } from "./TeamVacationPeriods";

// Dynamic import for heavy recharts component (code-splitting)
const TeamRankingLevelChart = dynamic(
  () =>
    import("./TeamRankingLevelChart").then((mod) => mod.TeamRankingLevelChart),
  {
    loading: () => (
      <div className="flex justify-center py-8">
        <Spinner size="md" />
      </div>
    ),
    ssr: false,
  },
);

interface TeamDrawerProps {
  team: Team | null;
  players: PublicUser[];
  isOpen: boolean;
  onClose: () => void;
  onChallenge?: (teamId: string) => void;
  canChallenge?: boolean;
  activeMatch?: Match | null;
  myTeamId?: string | null;
  myTeamName?: string | null;
  cardType?: "joker" | "braveheart";
  isSubmitting?: boolean;
  vacationStartsInDays?: number;
}

export function TeamDrawer({
  team,
  players,
  isOpen,
  onClose,
  onChallenge,
  canChallenge = false,
  vacationStartsInDays,
  activeMatch,
  myTeamId,
  myTeamName,
  cardType,
  isSubmitting = false,
}: TeamDrawerProps) {
  const t = useTranslations();

  const { tournament } = useTournamentContext();
  const { teams } = useTeamsContext();
  const { matches } = useMatchesContext();
  const { teamStatistics: opponentStatistics } = useTeamStatistics(team?.id);
  const { data: rankingHistory = [], isLoading: historyLoading } =
    useTeamRankingHistory(team?.id);

  // Check if team is on vacation or has future scheduled vacations
  const isOnVacation = team ? teamIsOnVacation(team) : false;
  const hasFutureVacations =
    team && team.vacations
      ? team.vacations.some((vacation) => new Date(vacation.start) > new Date())
      : false;
  const showStatusCard =
    team &&
    (team.status !== TeamStatus.AVAILABLE ||
      isOnVacation ||
      hasFutureVacations);

  // Filter team players
  const teamPlayers = useMemo(() => {
    if (!team) return [];
    return players.filter((p) => team.playerIds.includes(p.id));
  }, [team, players]);

  // Get opposing team name from active match
  const opposingTeamName = useMemo(() => {
    if (!activeMatch || !team) return null;
    const opposingTeamId =
      activeMatch.team1Id === team.id
        ? activeMatch.team2Id
        : activeMatch.team1Id;
    const opposingTeam = teams.find((t) => t.id === opposingTeamId);
    return opposingTeam?.name || null;
  }, [activeMatch, team, teams]);

  const teamMatches = useMemo(() => {
    if (!team) return [];
    // Only show played matches so this list matches the "matches played"
    // count in the statistics table. Pending challenges are surfaced
    // separately via activeMatch, and previously they were sorted to the top
    // here and pushed an older completed match past the 5-item cap.
    return matches
      .filter(
        (m) =>
          (m.team1Id === team.id || m.team2Id === team.id) &&
          m.status === MatchStatus.COMPLETED,
      )
      .sort((a, b) => {
        // Sort by date (newest first)
        if (a.matchDate && b.matchDate) {
          return b.matchDate.getTime() - a.matchDate.getTime();
        }
        // Matches without dates go after those with dates
        if (a.matchDate && !b.matchDate) return -1;
        if (!a.matchDate && b.matchDate) return 1;

        // Fallback to createdAt for matches without matchDate
        return b.createdAt.getTime() - a.createdAt.getTime();
      })
      .slice(0, 5);
  }, [team, matches]);

  // Prepare chart data from ranking history
  const chartData = useMemo(() => {
    if (!team || !tournament) return [];

    // The tournament goes live at startsAt. Setup-phase placement rows store a
    // fixed level that goes stale once a later placement shifts the team down
    // (see tournament-stats.ts), so plotting them misreports the starting
    // point. Keep only live rows and anchor the starting dot to the team's
    // real level just before its first live change.
    const liveFrom = tournament.startsAt
      ? new Date(tournament.startsAt).getTime()
      : 0;
    const liveRows = [...rankingHistory]
      .filter((entry) => entry.date.getTime() >= liveFrom)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (liveRows.length === 0) {
      // No movement yet: single dot (chart hides itself when length <= 1).
      return [
        {
          date: format(team.createdAt, "dd.MM"),
          level: team.calculatedPosition
            ? Math.ceil(team.calculatedPosition / tournament.towerTeamsPerRow)
            : 1,
          fullDate: team.createdAt,
        },
      ];
    }

    // Starting dot = level held before the first live change (= startingLevel
    // in the stats). previousTowerLevel on live rows is derived from the real
    // position, so it is accurate even when the placement row is stale.
    const startDate = tournament.startsAt
      ? new Date(tournament.startsAt)
      : liveRows[0].date;
    return [
      {
        date: format(startDate, "dd.MM"),
        level: liveRows[0].previousTowerLevel,
        fullDate: startDate,
      },
      ...liveRows.map((entry) => ({
        date: format(entry.date, "dd.MM"),
        level: entry.newTowerLevel,
        fullDate: entry.date,
      })),
    ];
  }, [rankingHistory, team, tournament]);

  // Calculate Y-axis domain (invert so level 1 is at top)
  const yAxisDomain = useMemo((): [number, number] => {
    if (chartData.length === 0) return [1, 10];
    const levels = chartData.map((d) => d.level);
    const maxLevel = Math.max(...levels);
    const minLevel = Math.min(...levels);
    return [Math.max(1, minLevel - 1), maxLevel + 1];
  }, [chartData]);

  if (!team) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom"
      size="4xl"
      hideCloseButton={true}
      motionProps={{
        variants: {
          enter: {
            y: 0,
            transition: {
              duration: 0.4,
              ease: [0.4, 0, 0.2, 1],
            },
          },
          exit: {
            y: "100%",
            transition: {
              duration: 0.4,
              ease: [0.4, 0, 0.2, 1],
            },
          },
        },
      }}
    >
      <DrawerContent className="bg-transparent border-0 max-h-[90vh]">
        {(closeDrawer) => (
          <div className="relative flex flex-col max-h-[90vh]">
            {/* Decorative top border */}
            <div className="absolute -top-0.5 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />

            {/* Main container */}
            <div className="relative bg-[#0f1219] rounded-t-2xl border-t border-x border-amber-500/20 overflow-hidden">
              {/* ========== HEADER SECTION ========== */}
              <div className="relative px-4 pt-4 pb-4 md:px-6 md:pt-5 border-b border-white/5">
                {/* Close button — top right of header */}
                <button
                  onClick={closeDrawer}
                  className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg border border-amber-500/20 bg-amber-500/5 text-amber-400/60 hover:bg-amber-500/10 hover:text-amber-400 transition-colors z-10"
                  aria-label="Close drawer"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>

                {/* Team name — centered */}
                <div className="flex items-center gap-3 justify-center pr-8">
                  <div className="h-px w-8 bg-gradient-to-r from-transparent to-amber-500/40 md:w-16" />
                  <h2 className="font-cinzel text-xl md:text-2xl font-bold text-amber-400 tracking-wide truncate">
                    {team.name || t("teams.anonymousTeam")}
                  </h2>
                  <div className="h-px w-8 bg-gradient-to-l from-transparent to-amber-500/40 md:w-16" />
                </div>

                {/* Inline status under team name */}
                {showStatusCard && tournament && (
                  <div className="mt-1.5 pr-8">
                    <TeamStatusCard
                      team={team}
                      activeMatch={activeMatch}
                      opposingTeamName={opposingTeamName}
                      variant="inline"
                    />
                  </div>
                )}
              </div>

              {/* ========== SCROLLABLE BODY ========== */}
              <div className="overflow-y-auto px-4 pt-3 pb-4 md:px-5 md:pb-5 max-h-[calc(90vh-180px)]">
                <div className="flex flex-col gap-4">
                  {/* ========== MAIN CONTENT GRID ========== */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* ========== FIRST COLUMN: ACHIEVEMENTS + TEAM ========== */}
                    <div className="flex flex-col gap-4">
                      {/* ========== VACATION PERIODS (shown first when set) ========== */}
                      {tournament && (
                        <TeamVacationPeriods
                          team={team}
                          tournament={tournament}
                        />
                      )}

                      {/* ========== ACHIEVEMENTS ========== */}
                      {tournament && (
                        <AchievementBadges
                          tournamentId={tournament.id}
                          teamId={team.id}
                          size="md"
                          variant="list"
                        />
                      )}

                      {/* ========== TEAM SECTION ========== */}
                      <div className="space-y-3">
                        {/* Section header */}
                      <div className="flex items-center gap-2">
                        <h3 className="text-xs font-medium text-amber-500/80 uppercase tracking-wider">
                          {t("teams.team")}
                        </h3>
                        <div className="flex-1 h-px bg-amber-500/20" />
                      </div>

                      {/* ========== COOLDOWN COUNTER (for own team) ========== */}
                      {tournament && team.id === myTeamId && (
                        <CooldownCounter
                          team={team}
                          tournament={tournament}
                          variant="card"
                        />
                      )}

                      {/* Players container */}
                      <div className="rounded-xl bg-[#0a0c10]/60 border border-amber-500/10 p-3">
                        <TeamDrawerPlayers
                          players={teamPlayers}
                          captainId={team.captainId}
                          hideNames={team.status === TeamStatus.FORFEIT}
                        />

                        {/* Special Cards Section */}
                        {tournament && (
                          <TeamDrawerCards
                            jokerCards={team.jokerCards}
                            braveheartCards={team.braveheartCards ?? 0}
                            tournament={tournament}
                          />
                        )}
                      </div>
                      </div>
                    </div>

                    {/* ========== LEVEL HISTORY SECTION (MIDDLE COLUMN) ========== */}
                    <div className="space-y-3">
                      <TeamRankingLevelChart
                        chartData={chartData}
                        yAxisDomain={yAxisDomain}
                        isLoading={historyLoading}
                      />

                      {/* Team Statistics below chart */}
                      {opponentStatistics && (
                        <TeamStatisticsComparison
                          teamName={team.name || t("teams.team")}
                          teamStatistics={opponentStatistics}
                        />
                      )}
                    </div>

                    {/* ========== RECENT MATCHES SECTION ========== */}
                    <div className="md:col-span-2 lg:col-span-1">
                      {/* Section header */}
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xs font-medium text-amber-500/80 uppercase tracking-wider">
                          {t("matches.recentMatches")}
                        </h3>
                        <div className="flex-1 h-px bg-amber-500/20" />
                      </div>

                      {/* Matches container */}
                      <div className="rounded-xl bg-[#0a0c10]/60 border border-amber-500/10 p-3">
                        {teamMatches.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-6 text-center">
                            <span className="text-xl mb-1.5 opacity-20">
                              ⚔️
                            </span>
                            <p className="text-xs text-white/40">
                              {t("matches.noMatches") ||
                                "No matches recorded yet"}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            {teamMatches.slice(0, 5).map((match) => (
                              <MatchResultRow
                                key={match.id}
                                match={match}
                                highlightTeamId={team.id}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ========== FOOTER WITH ACTIONS ========== */}
              {(canChallenge || vacationStartsInDays !== undefined) &&
                onChallenge && (
                  <div className="border-t border-white/10 bg-[#0a0c10]">
                    <div className="p-3 flex justify-center">
                      <div className="w-full md:w-[320px]">
                        <ChallengeButton
                          className="h-16 md:h-14"
                          targetTeam={team}
                          onChallenge={(teamId) => {
                            onChallenge(teamId);
                            closeDrawer();
                          }}
                          cardType={cardType}
                          isSubmitting={isSubmitting}
                          disabled={vacationStartsInDays !== undefined}
                          vacationStartsInDays={vacationStartsInDays}
                        />
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}
